import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import Card from "./Card";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

const getSeverityColor = (level) => {
    switch (level) {
        case "CRITICAL": return "text-red-700";
        case "HIGH": return "text-orange-600";
        case "MEDIUM": return "text-yellow-600";
        case "LOW": return "text-green-600";
        default: return "text-gray-600";
    }
};

const getSeverityBarColor = (level) => {
    switch (level) {
        case "CRITICAL": return "bg-red-700";
        case "HIGH": return "bg-orange-500";
        case "MEDIUM": return "bg-yellow-500";
        case "LOW": return "bg-green-500";
        default: return "bg-gray-400";
    }
};

const getBadgeBorderColor = (status) => {
    switch (status) {
        case "no_findings": return "border-green-400";
        case "findings_present": return "border-amber-400";
        default: return "border-gray-300 border-dashed";
    }
};

const getBadgeStatusLabel = (status) => {
    switch (status) {
        case "no_findings": return { text: "No findings", color: "text-green-600" };
        case "findings_present": return { text: "Findings present", color: "text-amber-600" };
        default: return { text: "Not assessed", color: "text-gray-400" };
    }
};

const VendorAiRiskModule = ({ vendorId }) => {
    const apiCall = useApi();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const result = await apiCall("GET", `/TPRMVendor/${vendorId}/ai-risk-summary`);
                setData(result);
            } catch (err) {
                setError("Failed to load AI risk data");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [vendorId]);

    if (loading) return (
        <Card>
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        </Card>
    );
    if (error) return <Card><p className="text-red-600 text-sm">{error}</p></Card>;
    if (!data) return null;

    return (
        <div className="space-y-6">
            {/* Header Row */}
            <Card>
                <div className="mb-6 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-purple-900 uppercase tracking-wide">
                        AI Risk Posture
                    </h3>
                    {data.totalAiRisks === 0 && (
                        <div className="p-2 px-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                            No AI risk findings yet. Complete a <Link to="/TPRMOrganizations" className="underline text-blue-700 hover:text-blue-900">vendor assessment</Link> to generate AI risk posture results.
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left: Severity Donut */}
                    <div className="flex flex-col items-center">
                        {(() => {
                            const severityColors = {
                                Critical: "#ef4444",
                                High: "#f97316",
                                Medium: "#facc15",
                                Low: "#4ade80",
                            };
                            const distribution = data.riskDistribution || {};
                            const severityData = [
                                { name: "Critical", value: distribution.critical || 0 },
                                { name: "High", value: distribution.high || 0 },
                                { name: "Medium", value: distribution.medium || 0 },
                                { name: "Low", value: distribution.low || 0 },
                            ];
                            const totalRisks = data.totalAiRisks || 0;
                            const chartData = totalRisks === 0
                                ? [{ name: "None", value: 1 }]
                                : severityData.filter(d => d.value > 0);

                            return (
                                <>
                                    <div className="relative" style={{ width: 220, height: 220 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={chartData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={100}
                                                    dataKey="value"
                                                    stroke="none"
                                                >
                                                    {chartData.map((entry, index) => (
                                                        <Cell
                                                            key={index}
                                                            fill={totalRisks === 0 ? "#d1d5db" : severityColors[entry.name]}
                                                        />
                                                    ))}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                                            <span className="text-3xl font-bold text-gray-800">{totalRisks}</span>
                                            <span className="text-xs text-gray-500">Total Risks</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2 mb-3">
                                        {severityData.map((entry) => (
                                            <div key={entry.name} className="flex items-center gap-2 text-sm">
                                                <span
                                                    className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: severityColors[entry.name] }}
                                                />
                                                <span className="text-gray-600">{entry.name}</span>
                                                <span className="font-semibold text-gray-800">{entry.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            );
                        })()}
                        <div className="flex justify-center space-x-2 mb-2">
                            <span className={`text-sm px-3 py-1 rounded-full font-semibold ${
                                data.aiRiskLevel === "CRITICAL" ? "text-red-700 bg-red-100" :
                                data.aiRiskLevel === "HIGH" ? "text-orange-600 bg-orange-100" :
                                data.aiRiskLevel === "MEDIUM" ? "text-yellow-600 bg-yellow-100" :
                                "text-green-600 bg-green-100"
                            }`}>
                                {data.aiRiskLevel}
                            </span>
                        </div>
                        <div className="text-sm text-gray-600 text-center">
                            <span className="font-semibold">{data.totalAiRisks}</span> AI Risk{data.totalAiRisks !== 1 ? "s" : ""}
                        </div>
                    </div>

                    {/* Right: Framework Badges */}
                    <div>
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">
                            Frameworks
                        </h4>
                        <p className="text-xs text-gray-400 mb-3">
                            Framework counts show risks tagged to specific AI governance frameworks. The donut total includes all AI-type risks regardless of framework attribution — one risk may map to multiple frameworks, so counts don't sum to the total.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            {data.frameworkBadges?.map((badge) => {
                                const statusInfo = getBadgeStatusLabel(badge.status);
                                return (
                                    <div
                                        key={badge.framework}
                                        className={`p-3 rounded-lg border-2 bg-purple-50 ${getBadgeBorderColor(badge.status)}`}
                                    >
                                        <div className="text-sm font-semibold text-purple-800 mb-1">
                                            {badge.framework}
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            {badge.riskCount} risk{badge.riskCount !== 1 ? "s" : ""}
                                            {badge.controlCount > 0 && (
                                                <span className="ml-1">
                                                    &middot; {badge.controlCount} control{badge.controlCount !== 1 ? "s" : ""}
                                                </span>
                                            )}
                                        </div>
                                        <div className={`text-xs mt-1 font-medium ${statusInfo.color}`}>
                                            {badge.status === "no_findings" && <span className="mr-1">&#10003;</span>}
                                            {statusInfo.text}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Category Breakdown - hidden until riskCategory is populated by AI prompt (all default to "General") */}
        </div>
    );
};

export default VendorAiRiskModule;
